import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  ArrowLeft, 
  Building2, 
  Plus, 
  Check, 
  RefreshCw,
  CreditCard,
  Wallet,
  TrendingUp,
  Shield,
  Loader2,
  Link2,
  Unlink,
  Eye,
  EyeOff
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mock bank data
const MOCK_BANKS = [
  { id: "chase", name: "Chase", logo: "🏦", color: "bg-blue-600" },
  { id: "bofa", name: "Bank of America", logo: "🏛️", color: "bg-red-600" },
  { id: "wells", name: "Wells Fargo", logo: "🏧", color: "bg-yellow-600" },
  { id: "citi", name: "Citibank", logo: "🏦", color: "bg-blue-500" },
  { id: "capital", name: "Capital One", logo: "💳", color: "bg-orange-600" }
];

const MOCK_ACCOUNTS = [
  { id: "1", bank_id: "chase", name: "Chase Checking", type: "checking", balance: 4523.67, mask: "4521" },
  { id: "2", bank_id: "chase", name: "Chase Savings", type: "savings", balance: 12450.00, mask: "8834" },
  { id: "3", bank_id: "bofa", name: "BofA Credit Card", type: "credit", balance: -1245.30, mask: "9012" }
];

const BankConnectionPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connectingBank, setConnectingBank] = useState(null);
  const [showBalances, setShowBalances] = useState(true);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (!storedUser) {
      navigate("/auth");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    fetchConnectedAccounts(userData.id);
  }, [navigate]);

  const fetchConnectedAccounts = async (userId) => {
    try {
      const response = await axios.get(`${API}/bank/accounts/${userId}`);
      setConnectedAccounts(response.data.accounts || []);
    } catch (error) {
      // If no accounts, use empty array
      setConnectedAccounts([]);
    }
  };

  const handleConnectBank = async (bank) => {
    setConnectingBank(bank.id);
    
    // Simulate Plaid Link flow
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    try {
      // Mock: Add accounts from this bank
      const mockAccounts = MOCK_ACCOUNTS.filter(acc => acc.bank_id === bank.id);
      
      await axios.post(`${API}/bank/connect`, {
        user_id: user.id,
        bank_id: bank.id,
        bank_name: bank.name,
        accounts: mockAccounts
      });
      
      toast.success(`${bank.name} connected successfully!`);
      fetchConnectedAccounts(user.id);
      setLinkDialogOpen(false);
    } catch (error) {
      toast.error("Failed to connect bank. Please try again.");
    } finally {
      setConnectingBank(null);
    }
  };

  const handleDisconnectBank = async (bankId) => {
    try {
      await axios.delete(`${API}/bank/disconnect/${user.id}/${bankId}`);
      toast.success("Bank disconnected");
      fetchConnectedAccounts(user.id);
    } catch (error) {
      toast.error("Failed to disconnect bank");
    }
  };

  const handleRefreshAccounts = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Simulate balance updates
    setConnectedAccounts(prev => prev.map(acc => ({
      ...acc,
      balance: acc.balance + (Math.random() - 0.5) * 100,
      last_updated: new Date().toISOString()
    })));
    
    toast.success("Accounts refreshed");
    setLoading(false);
  };

  const totalBalance = connectedAccounts.reduce((sum, acc) => sum + acc.balance, 0);
  const connectedBanks = [...new Set(connectedAccounts.map(acc => acc.bank_id))];

  const formatBalance = (amount) => {
    if (!showBalances) return "••••••";
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/settings")}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-semibold">Connected Banks</h1>
              <p className="text-xs text-muted-foreground">Manage your linked accounts</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowBalances(!showBalances)}
            >
              {showBalances ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
            </Button>
            {connectedAccounts.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefreshAccounts}
                disabled={loading}
              >
                <RefreshCw className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} />
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Total Balance */}
        {connectedAccounts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="border-tamba-teal/30 bg-gradient-to-br from-tamba-teal/5 to-transparent">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-tamba-teal/20 flex items-center justify-center">
                    <Wallet className="h-7 w-7 text-tamba-teal" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Net Worth</p>
                    <p className="font-heading text-3xl font-bold">{formatBalance(totalBalance)}</p>
                    <p className="text-xs text-muted-foreground">{connectedBanks.length} banks • {connectedAccounts.length} accounts</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Connect Bank Button */}
        <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="w-full rounded-xl py-6 bg-tamba-blue hover:bg-tamba-blue/90 gap-2"
              data-testid="connect-bank-btn"
            >
              <Plus className="h-5 w-5" />
              Connect a Bank Account
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Link2 className="h-5 w-5 text-tamba-blue" />
                Connect Your Bank
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground mb-4">
                Select your bank to securely link your accounts. Your credentials are never stored.
              </p>
              <div className="space-y-2">
                {MOCK_BANKS.map((bank) => (
                  <button
                    key={bank.id}
                    onClick={() => handleConnectBank(bank)}
                    disabled={connectingBank !== null || connectedBanks.includes(bank.id)}
                    className={`w-full p-4 rounded-xl border-2 flex items-center gap-4 transition-all ${
                      connectedBanks.includes(bank.id)
                        ? "border-green-500/50 bg-green-50/50 cursor-not-allowed"
                        : "border-border hover:border-tamba-blue/50 hover:bg-secondary/50"
                    }`}
                    data-testid={`bank-${bank.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${bank.color} flex items-center justify-center text-white text-xl`}>
                      {bank.logo}
                    </div>
                    <span className="font-medium flex-1 text-left">{bank.name}</span>
                    {connectingBank === bank.id ? (
                      <Loader2 className="h-5 w-5 animate-spin text-tamba-blue" />
                    ) : connectedBanks.includes(bank.id) ? (
                      <Check className="h-5 w-5 text-green-600" />
                    ) : null}
                  </button>
                ))}
              </div>
              <div className="mt-4 p-3 bg-secondary/50 rounded-lg flex items-start gap-2">
                <Shield className="h-4 w-4 text-tamba-teal mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  Bank-level security. We use Plaid to securely connect your accounts without storing your login credentials.
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Connected Accounts */}
        {connectedAccounts.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h2 className="font-heading text-lg font-semibold mb-4">Your Accounts</h2>
            <div className="space-y-3">
              {connectedAccounts.map((account, index) => {
                const bank = MOCK_BANKS.find(b => b.id === account.bank_id);
                return (
                  <motion.div
                    key={account.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="border-border/50 hover:shadow-md transition-all">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg ${bank?.color || "bg-gray-500"} flex items-center justify-center text-white`}>
                            {account.type === "credit" ? (
                              <CreditCard className="h-5 w-5" />
                            ) : account.type === "savings" ? (
                              <TrendingUp className="h-5 w-5" />
                            ) : (
                              <Building2 className="h-5 w-5" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{account.name}</p>
                            <p className="text-xs text-muted-foreground">
                              ••••{account.mask} • {account.type}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className={`font-semibold ${account.balance < 0 ? "text-tamba-coral" : "text-foreground"}`}>
                              {formatBalance(account.balance)}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            {/* Disconnect Banks */}
            <div className="mt-6">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Manage Connections</h3>
              <div className="space-y-2">
                {connectedBanks.map(bankId => {
                  const bank = MOCK_BANKS.find(b => b.id === bankId);
                  return (
                    <div key={bankId} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg ${bank?.color || "bg-gray-500"} flex items-center justify-center text-white text-sm`}>
                          {bank?.logo}
                        </div>
                        <span className="font-medium text-sm">{bank?.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDisconnectBank(bankId)}
                        className="text-muted-foreground hover:text-tamba-coral gap-1"
                      >
                        <Unlink className="h-4 w-4" />
                        Disconnect
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="border-dashed border-2 border-border/50">
              <CardContent className="py-12 text-center">
                <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="font-semibold mb-2">No Banks Connected</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Link your bank accounts to see all your finances in one place
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Info Card */}
        <Card className="border-tamba-yellow/30 bg-gradient-to-br from-tamba-yellow/5 to-transparent">
          <CardContent className="p-4">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <Shield className="h-4 w-4 text-tamba-yellow" />
              About Bank Connections
            </h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Automatic transaction import (coming soon)</li>
              <li>• Real-time balance updates</li>
              <li>• Your data is encrypted and secure</li>
              <li>• Disconnect anytime with one click</li>
            </ul>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default BankConnectionPage;
